/**
 * Calendar-anchored "stops" for the failure-clusters history slider.
 *
 * A given `window_end` renders that window's traffic through today's taxonomy
 * (trailing 1-day / 7-day window ending at `as_of`). The scrubber exposes that
 * in two parts so the reachable range is always visible (#1261):
 *
 *  - a **period selector** — a month (daily) / a year (weekly), bounded by the
 *    domain's earliest data (`available_from`) and the current period; and
 *  - a **slider** over that period's stops — one per complete UTC calendar day
 *    (daily) / complete ISO week, Monday 00:00 UTC (weekly).
 *
 * A day's stop ends at the next UTC midnight, a week's at the next Monday —
 * sent as `as_of`, the backend's trailing window then covers exactly that
 * day/week. The current period additionally carries the live "now" stop
 * (`as_of` omitted → the backend's period-to-date: today / this ISO week so
 * far) as its right-most stop; the partial "today"/"this week" never appears as
 * a complete stop. Labels are computed
 * here in UTC — the ui `formatDate` uses local getters, which would shift a
 * midnight-aligned boundary onto the wrong day in non-UTC timezones.
 * Pure + unit-tested.
 */

import { parseMonth, SHORT_MONTH_NAMES } from "../../usage/utils/months";
import type { Granularity } from "../types";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const STEP_MS: Record<Granularity, number> = { daily: DAY_MS, weekly: WEEK_MS };

export interface TimeStop {
  /** Inclusive window start, as epoch ms (start of the UTC day / ISO week). */
  start: number;
  /** Exclusive window end, as epoch ms — the `as_of` this stop maps to. */
  end: number;
  /** True for the right-most stop of the current period — the live "now" view. */
  isLive: boolean;
  /** Display label, UTC: "as of 2026/06/03" / "week of Jun 1 – Jun 7"; the live stop shows today's date, "2026/06/11". */
  label: string;
}

/** A selectable period: "YYYY-MM" (daily) or "YYYY" (weekly). */
export type Period = string;

const utcMonthOf = (ms: number): Period => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

const utcDayStart = (ms: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Monday 00:00 UTC of the ISO week containing `ms`. */
const isoWeekStart = (ms: number): number => {
  const dayStart = utcDayStart(ms);
  return dayStart - ((new Date(dayStart).getUTCDay() + 6) % 7) * DAY_MS;
};

/** "YYYY/MM/DD" for the UTC day containing `ms`. */
const utcDateLabel = (ms: number): string => {
  const d = new Date(ms);
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}/${month}/${day}`;
};

/** "as of YYYY/MM/DD" for the UTC day starting at `dayStart`. */
const dayLabel = (dayStart: number): string => `as of ${utcDateLabel(dayStart)}`;

/** "week of Jun 1 – Jun 7" for the ISO week starting at `weekStart`. */
const weekLabel = (weekStart: number): string => {
  const fmt = (ms: number) => {
    const d = new Date(ms);
    return `${SHORT_MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
  };
  return `week of ${fmt(weekStart)} – ${fmt(weekStart + 6 * DAY_MS)}`;
};

/** The period containing `now`, in UTC. */
export function currentPeriod(
  granularity: Granularity,
  now: number = Date.now(),
): Period {
  return granularity === "daily"
    ? utcMonthOf(now)
    : String(new Date(now).getUTCFullYear());
}

/** The period containing instant `ms`. A week belongs to its Monday's year. */
function periodOf(granularity: Granularity, ms: number): Period {
  return granularity === "daily"
    ? utcMonthOf(ms)
    : String(new Date(isoWeekStart(ms)).getUTCFullYear());
}

/**
 * All selectable periods, newest → oldest (dropdown order): from the period
 * containing `availableFrom` up to the current one. Periods with no stops are
 * dropped — e.g. when `availableFrom` sits in a year-boundary ISO week that is
 * not yet complete, the week's Monday puts its year in range but that year has
 * nothing to select.
 */
export function periodOptions(
  granularity: Granularity,
  availableFrom: string,
  now: number = Date.now(),
): Period[] {
  const from = Math.min(new Date(availableFrom).getTime(), now);
  const oldest = periodOf(granularity, from);
  const periods: Period[] = [];
  if (granularity === "daily") {
    let { year, month } = parseMonth(currentPeriod("daily", now));
    const stop = parseMonth(oldest);
    while (year > stop.year || (year === stop.year && month >= stop.month)) {
      periods.push(`${year}-${String(month).padStart(2, "0")}`);
      month -= 1;
      if (month === 0) {
        month = 12;
        year -= 1;
      }
    }
  } else {
    for (let y = new Date(now).getUTCFullYear(); y >= parseInt(oldest, 10); y--) {
      periods.push(String(y));
    }
  }
  return periods.filter(
    (p) => computeStops(granularity, p, availableFrom, now).length > 0,
  );
}

/**
 * The period a committed `asOf` belongs to: null → the current period; else
 * the period containing `end − 1` (`as_of` is an exclusive boundary, so a
 * midnight-aligned value belongs to the day/week it closes). Clamped into the
 * selectable range.
 */
export function periodForValue(
  granularity: Granularity,
  asOf: string | null,
  availableFrom: string,
  now: number = Date.now(),
): Period {
  const current = currentPeriod(granularity, now);
  if (asOf === null) return current;
  const period = periodOf(granularity, new Date(asOf).getTime() - 1);
  const oldest = periodOf(
    granularity,
    Math.min(new Date(availableFrom).getTime(), now),
  );
  // "YYYY-MM" / "YYYY" order lexicographically as chronologically.
  if (period > current) return current;
  if (period < oldest) return oldest;
  return period;
}

/**
 * One period's stops, oldest → newest: each complete UTC day of the month
 * (daily) / each complete ISO week whose Monday falls in the year (weekly),
 * clamped to the day/week containing `availableFrom` on the left and to
 * complete-by-`now` on the right. The current period gets the live "now"
 * stop appended; a period of pure future days/weeks (never selectable)
 * yields none.
 */
export function computeStops(
  granularity: Granularity,
  period: Period,
  availableFrom: string,
  now: number = Date.now(),
): TimeStop[] {
  const from = new Date(availableFrom).getTime();
  let start: number; // first candidate stop's window start
  let periodEnd: number; // exclusive bound on window starts in this period
  if (granularity === "daily") {
    const { year, month } = parseMonth(period);
    start = Math.max(Date.UTC(year, month - 1, 1), utcDayStart(from));
    periodEnd = Date.UTC(year, month, 1);
  } else {
    const year = parseInt(period, 10);
    const jan1 = Date.UTC(year, 0, 1);
    let firstMonday = isoWeekStart(jan1);
    if (firstMonday < jan1) firstMonday += WEEK_MS;
    start = Math.max(firstMonday, isoWeekStart(from));
    periodEnd = Date.UTC(year + 1, 0, 1);
  }

  const step = STEP_MS[granularity];
  const stops: TimeStop[] = [];
  for (; start < periodEnd && start + step <= now; start += step) {
    stops.push({
      start,
      end: start + step,
      isLive: false,
      label: granularity === "daily" ? dayLabel(start) : weekLabel(start),
    });
  }
  if (period === currentPeriod(granularity, now)) {
    stops.push({
      start: now - step,
      end: now,
      isLive: true,
      label: utcDateLabel(now),
    });
  }
  return stops;
}

/**
 * The `as_of` for a period's most recent stop — what selecting it in the
 * dropdown commits: null (live) for the current period, else the end of its
 * last complete day/week.
 */
export function latestStopValue(
  granularity: Granularity,
  period: Period,
  availableFrom: string,
  now: number = Date.now(),
): string | null {
  const stops = computeStops(granularity, period, availableFrom, now);
  return stops.length ? valueForStop(stops[stops.length - 1]) : null;
}

/**
 * Re-snap an `asOf` onto another granularity's stop grid (the page-level
 * daily/weekly switch): null stays live; else the nearest stop in the period
 * containing `asOf` under the new granularity.
 */
export function snapValue(
  granularity: Granularity,
  asOf: string | null,
  availableFrom: string,
  now: number = Date.now(),
): string | null {
  if (asOf === null) return null;
  const period = periodForValue(granularity, asOf, availableFrom, now);
  const stops = computeStops(granularity, period, availableFrom, now);
  if (!stops.length) return null;
  return valueForStop(stops[indexForValue(stops, asOf)]);
}

/**
 * Index of the stop for `asOf` (null = live = the last stop); the nearest stop
 * otherwise, so values minted by an older grid (or another granularity) still
 * resolve.
 */
export function indexForValue(stops: TimeStop[], asOf: string | null): number {
  if (asOf === null) return stops.length - 1;
  const t = new Date(asOf).getTime();
  let best = 0;
  let bestDist = Infinity;
  stops.forEach((stop, i) => {
    const dist = Math.abs(stop.end - t);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  });
  return best;
}

/** The `as_of` a stop maps to: null for the live edge, else a UTC ISO string. */
export function valueForStop(stop: TimeStop): string | null {
  return stop.isLive ? null : new Date(stop.end).toISOString();
}
